import { Box, Button, Container, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { HomeOutlined, DashboardOutlined, SearchOutlined, ArrowBackOutlined } from '@mui/icons-material';
import { motion, MotionConfig } from 'framer-motion';
import { paths } from '../routes/paths';
import PageMetadata from '../components/SEO/PageMetadata';
import CheckInRush from '../components/CheckInRush';

export default function NotFoundPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const isAdminRoute = location.pathname.startsWith('/manage');

    const handleGoHome = () => {
        navigate(isAdminRoute ? paths.dashboard : paths.public.root);
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleSearchEvents = () => {
        navigate(paths.public.searchEvents);
    };

    return (
        <MotionConfig reducedMotion="user">
            <PageMetadata
                title="Page Not Found | AttendIn"
                description="The page you're looking for doesn't exist. Return to the AttendIn home page or use search to find what you need."
                robots="noindex,follow"
            />
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, 
                    ${theme.palette.primary.main}15 0%, 
                    ${theme.palette.secondary.main}10 50%, 
                    ${theme.palette.primary.main}08 100%)`,
                    position: 'relative',
                    overflow: 'hidden',
                    py: 4,
                }}
            >
                {/* Animated background orbs */}
                <Box
                    component={motion.div}
                    animate={reducedMotion ? undefined : {
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                    }}
                    transition={{
                        duration: 8,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    sx={{
                        position: 'absolute',
                        top: '-10%',
                        right: '-5%',
                        width: '40%',
                        height: '40%',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${theme.palette.primary.main}40 0%, transparent 70%)`,
                        filter: 'blur(60px)',
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    component={motion.div}
                    animate={reducedMotion ? undefined : {
                        scale: [1, 1.3, 1],
                        opacity: [0.2, 0.4, 0.2],
                    }}
                    transition={{
                        duration: 10,
                        repeat: Infinity,
                        ease: 'easeInOut',
                        delay: 1,
                    }}
                    sx={{
                        position: 'absolute',
                        bottom: '-10%',
                        left: '-5%',
                        width: '50%',
                        height: '50%',
                        borderRadius: '50%',
                        background: `radial-gradient(circle, ${theme.palette.secondary.main}40 0%, transparent 70%)`,
                        filter: 'blur(60px)',
                        pointerEvents: 'none',
                    }}
                />

                <Container maxWidth="md">
                    <Paper
                        component={motion.div}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                        elevation={0}
                        sx={{
                            p: { xs: 3, md: 5 },
                            borderRadius: 4,
                            background: `linear-gradient(145deg, 
                            ${theme.palette.background.paper}95 0%, 
                            ${theme.palette.background.paper}85 100%)`,
                            backdropFilter: 'blur(20px)',
                            border: `1px solid ${theme.palette.divider}30`,
                            boxShadow: `0 8px 32px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.4)' : 'rgba(0,0,0,0.1)'}`,
                            textAlign: 'center',
                            position: 'relative',
                            overflow: 'hidden',
                        }}
                    >
                        {/* Logo with animation */}
                        <Box
                            component={motion.div}
                            initial={{ scale: 0.8, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            sx={{ mb: 3 }}
                        >
                            <img
                                src="/logo-only.png"
                                alt="AttendIn Logo"
                                style={{
                                    height: '80px',
                                    opacity: 0.9,
                                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.1))',
                                }}
                            />
                        </Box>

                        {/* 404 Number with gradient */}
                        <Typography
                            component="h1"
                            variant="h4"
                            sx={{
                                mb: 2,
                                color: 'text.primary',
                            }}
                        >
                            404 - This event seems to have gone missing.
                        </Typography>

                        {/* Main heading */}
                        {isAdminRoute && (
                            <Typography
                                component={motion.div}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.5, delay: 0.4 }}
                                variant="h5"
                                sx={{
                                    mb: 2,
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                Management page not found
                            </Typography>
                        )}

                        {/* Description */}
                        <Typography
                            component={motion.div}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.5, delay: 0.5 }}
                            variant="body1"
                            sx={{
                                mb: 3,
                                color: theme.palette.text.secondary,
                                mx: 'auto',
                            }}
                        >
                            While we find that page, help manage attendee check-ins.
                        </Typography>

                        <CheckInRush />

                        {/* Action buttons */}
                        <Box
                            component={motion.div}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.6 }}
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 2,
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={isAdminRoute ? <DashboardOutlined /> : <HomeOutlined />}
                                onClick={handleGoHome}
                                sx={{
                                    px: 4,
                                    py: 1.5,
                                    minWidth: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                {isAdminRoute ? 'Go to dashboard' : 'Return home'}
                            </Button>

                            {!isAdminRoute && (
                                <Button
                                    variant="outlined"
                                    size="large"
                                    startIcon={<SearchOutlined />}
                                    onClick={handleSearchEvents}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        minWidth: { xs: '100%', sm: 'auto' },
                                    }}
                                >
                                    Search events
                                </Button>
                            )}

                            <Button
                                variant="text"
                                size="large"
                                startIcon={<ArrowBackOutlined />}
                                onClick={handleGoBack}
                                sx={{
                                    px: 3,
                                    py: 1.5,
                                    minWidth: { xs: '100%', sm: 'auto' },
                                }}
                            >
                                Go back
                            </Button>
                        </Box>

                        {/* Decorative bottom accent */}
                        <Box
                            component={motion.div}
                            animate={reducedMotion ? undefined : {
                                width: ['0%', '100%'],
                            }}
                            transition={{
                                duration: 1.5,
                                delay: 0.7,
                                ease: 'easeOut',
                            }}
                            sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                height: '4px',
                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                borderRadius: '0 0 16px 16px',
                            }}
                        />
                    </Paper>

                    {/* Helpful suggestion text */}
                    <Typography
                        component={motion.div}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.8 }}
                        variant="body2"
                        sx={{
                            mt: 4,
                            color: theme.palette.text.secondary,
                            textAlign: 'center',
                        }}
                    >
                        Lost? Try using the navigation menu or search to find what you need.
                    </Typography>
                </Container>
            </Box>
        </MotionConfig>
    );
}
